import supabase from "@/supabaseConfig"
import {createLogger} from "../../../logger"

/**
 * Search messages by optional text-search, participant filters, and date ranges.
 *
 * This function builds and executes a Supabase query with various filters:
 * - Text search via ILIKE and full-text search
 * - Participant filtering via group_ids
 * - Date range filtering for created_at and sent_at fields
 *
 * Results are paginated and include thread association data.
 */
export async function searchMessages({
  searchTerm,
  dateRanges,
  participantProfileIds,
  page = 0,
  limit = 100,
}: {
  searchTerm?: string
  dateRanges?: {start: string; end: string}[]
  participantProfileIds?: string[]
  page?: number
  limit?: number
}): Promise<{
  data: {thread_uid: string; message_id: string}[] | null
  error: Error | null
  count: number | null
  hasMore: boolean
}> {
  // Set up pagination
  const offset = page * limit

  // Set up a structured logger
  const logger = createLogger("SearchMessages")

  /**
   * STEP 1: Process participant filters (if any)
   * Find all group_ids that contain the specified participants
   */
  let filteredGroupIds: string[] | undefined
  if (participantProfileIds && participantProfileIds.length > 0) {
    logger.info(
      `Processing ${participantProfileIds.length} participant filters`
    )

    try {
      // Validate participant profile IDs and filter out invalid ones
      if (
        participantProfileIds.some(
          (id) => !id || id === "undefined" || id === "null"
        )
      ) {
        participantProfileIds = participantProfileIds.filter(
          (id) => id && id !== "undefined" && id !== "null"
        )

        // Early return if no valid IDs remain
        if (participantProfileIds.length === 0) {
          logger.info("No valid profile IDs after filtering")
          return {
            data: [],
            error: null,
            count: 0,
            hasMore: false,
          }
        }
      }

      // Find groups where participants are members
      const {data: groupParticipantsData, error: gpError} = await supabase
        .from("group_participants")
        .select("group_id")
        .in("profile_id", participantProfileIds)

      // Handle query errors
      if (gpError) {
        logger.error("Error fetching group participants", {error: gpError})
        return {
          data: null,
          error: gpError,
          count: null,
          hasMore: false,
        }
      }

      if (!groupParticipantsData || groupParticipantsData.length === 0) {
        logger.info(
          "No matching groups found via participants, trying sender_id"
        )

        // Fallback approach - search for messages sent by these participants
        const {data: senderMessagesData, error: smError} = await supabase
          .from("messages")
          .select("group_id")
          .in("sender_id", participantProfileIds)
          .limit(500)

        if (smError || !senderMessagesData || senderMessagesData.length === 0) {
          logger.info("No messages found with these participants as senders")
          return {
            data: [],
            error: null,
            count: 0,
            hasMore: false,
          }
        }

        // Extract unique group IDs from message sender data
        filteredGroupIds = [
          ...new Set(senderMessagesData.map((m) => m.group_id)),
        ]
      } else {
        // Extract unique group IDs from participants data
        filteredGroupIds = [
          ...new Set(groupParticipantsData.map((g) => g.group_id)),
        ]
      }

      // Limit the number of group IDs to prevent URL length issues
      if (filteredGroupIds.length > 100) {
        logger.info(
          `Limiting ${filteredGroupIds.length} group IDs to 100 to prevent URL length issues`
        )
        filteredGroupIds = filteredGroupIds.slice(0, 100)
      }
    } catch (error) {
      logger.error("Error processing participant filters", {error})
      // Don't return immediately, continue with other filters
    }
  }

  /**
   * STEP 2: Build the base query
   * Start with the join between messages and thread_messages
   */
  logger.info("Building query with filters")
  let query = supabase.from("messages").select(
    `
      message_id,
      thread_messages!inner(thread_uid)
    `,
    {count: "exact"} // Get total count for pagination
  )

  /**
   * STEP 3: Add text search filters (if applicable)
   * Uses both full-text search and ILIKE for better results
   */
  if (searchTerm) {
    // 1. Prepare the term for Postgres full-text search with prefix matching
    const formattedSearch = searchTerm
      .trim()
      .split(/\s+/)
      .map((term) => term + ":*") // Add prefix wildcard
      .join(" & ") // AND operator between terms

    // 2. Also prepare for ILIKE case-insensitive search
    // This catches content that might not be properly indexed by full-text search
    const ilikeTerm = `%${searchTerm.trim().toLowerCase()}%`

    // Combine both search approaches with OR
    query = query.or(
      `raw_content.ilike.${ilikeTerm},raw_content.wfts.${formattedSearch}`
    )

    logger.info(`Added text search for: "${searchTerm}"`)
  }

  /**
   * STEP 4: Add participant (group) filters (if applicable)
   * Uses groups that participants belong to for filtering
   */
  if (filteredGroupIds && filteredGroupIds.length > 0) {
    logger.info(`Adding group filter with ${filteredGroupIds.length} groups`)

    // Ensure we don't exceed URL length limits
    if (filteredGroupIds.length > 100) {
      filteredGroupIds = filteredGroupIds.slice(0, 100)
    }

    /**
     * Skip enhanced participant search due to API errors
     * Just use the group_id filtering which is more reliable
     */
    logger.info(
      "Using direct group_id filtering for participants (skipping profile search)"
    )

    // Always apply the basic group_id filter to ensure we get relevant results
    // This ensures we still have a filter even if the enhanced approach had issues
    query = query.in("group_id", filteredGroupIds)
  }

  /**
   * STEP 5: Add date range filters (if applicable)
   * Handles both string dates and complex date objects
   *
   * Note: Date filtering requires special care - we need to check both
   * the created_at and sent_at fields to handle different message types.
   */
  if (dateRanges && dateRanges.length > 0) {
    logger.info(`Adding ${dateRanges.length} date range filters`)

    // For multiple date ranges, we need to OR them together
    const dateRangeFilters = []

    // Process each date range separately
    for (const dr of dateRanges) {
      // Parse and normalize date inputs
      let startDate, endDate

      // Handle complex date object from DateInput component
      if (typeof dr.start === "object" && dr.start !== null) {
        const start = dr.start as any
        // Set to start of day (00:00:00)
        startDate = new Date(start.year, start.month - 1, start.day, 0, 0, 0)
      } else {
        // Handle string date with time component
        startDate = new Date(`${dr.start}T00:00:00`)
      }

      // Similar handling for end date, but set to end of day
      if (typeof dr.end === "object" && dr.end !== null) {
        const end = dr.end as any
        // Set to end of day (23:59:59.999)
        endDate = new Date(end.year, end.month - 1, end.day, 23, 59, 59, 999)
      } else {
        // Handle string date with time component
        endDate = new Date(`${dr.end}T23:59:59.999`)
      }

      // Build query filter that checks both created_at and sent_at
      // Each message must have either created_at OR sent_at within the range
      // NOTE: Must be a single line without newlines for PostgREST
      dateRangeFilters.push(
        `or(and(created_at.gte.${startDate.toISOString()},created_at.lte.${endDate.toISOString()}),and(sent_at.gte.${startDate.toISOString()},sent_at.lte.${endDate.toISOString()}))`
      )
    }

    // Apply all date filters with OR between different ranges (user wants any matching range)
    if (dateRangeFilters.length > 0) {
      const combinedFilter = dateRangeFilters.join(",")
      query = query.or(combinedFilter)

      logger.info("Added date range filters to query")
    }
  }

  /**
   * STEP 6: Add ordering and pagination
   * Ensure consistent ordering by date for proper pagination
   */
  // Sort by latest messages first (descending order)
  // Try both created_at and sent_at fields, prioritizing sent_at if available
  query = query
    .order("sent_at", {ascending: false, nullsFirst: false})
    .order("created_at", {ascending: false, nullsFirst: false})

  // Apply range-based pagination
  query = query.range(offset, offset + limit - 1)

  // Execute the final query
  logger.info(`Executing query with pagination: page ${page}, limit ${limit}`)
  const {data, error, count} = await query

  // Handle query errors
  if (error) {
    logger.error("Error executing query", {error})
    return {data: null, error, count: null, hasMore: false}
  }

  /**
   * STEP 7: Transform results into the expected format
   * Extracts thread_uid from the nested thread_messages relationship
   */
  const transformedData =
    data?.map((item) => {
      const threadUid = item.thread_messages?.[0]?.thread_uid
      return {
        message_id: item.message_id,
        thread_uid: threadUid,
      }
    }) || []

  // Determine if there are more results for pagination
  const hasMore = !!count && offset + limit < count

  // Log search success with result summary
  logger.info(
    `Search complete: found ${transformedData.length} results${count ? ` of ${count} total` : ""}, hasMore: ${hasMore}`
  )

  return {
    data: transformedData,
    error: null,
    count: count ?? 0,
    hasMore,
  }
}
